/**
 * @file 日期工具（YYYY-MM-DD）
 * @description DatePicker / DateRangePicker 共用的轻量日期格式与日历矩阵。
 */

/** 格式化为 `YYYY-MM-DD` */
export function formatDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * 解析 `YYYY-MM-DD`；非法则返回 null
 * @param value - 日期字符串
 */
export function parseDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2]) - 1
  const day = Number(match[3])
  const date = new Date(year, month, day)
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null
  }
  return date
}

/** 是否同一天（本地时区） */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

/** 是否 a 早于 b（按日） */
export function isBeforeDay(a: Date, b: Date): boolean {
  return formatDate(a) < formatDate(b)
}

/** 是否在闭区间内（按日） */
export function isInRange(day: Date, from: Date, to: Date): boolean {
  const key = formatDate(day)
  return key >= formatDate(from) && key <= formatDate(to)
}

/**
 * 月份加减
 * @param date - 基准日
 * @param delta - 月偏移
 */
export function addMonths(date: Date, delta: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + delta, 1)
}

/**
 * 生成某月日历矩阵（周一列起，空位为 null）
 * @param view - 当前视图月（取年月）
 */
export function getMonthCells(view: Date): (Date | null)[] {
  const year = view.getFullYear()
  const month = view.getMonth()
  const first = new Date(year, month, 1)
  // 周一为一周起点：周日=0 → 6，周一=1 → 0 …
  const startPad = (first.getDay() + 6) % 7
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells: (Date | null)[] = []
  for (let i = 0; i < startPad; i++) cells.push(null)
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push(new Date(year, month, day))
  }
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

/** 中文月份标题 */
export function formatMonthTitle(date: Date): string {
  return `${date.getFullYear()}年${date.getMonth() + 1}月`
}

export const WEEKDAY_LABELS = ['一', '二', '三', '四', '五', '六', '日'] as const
